import { Roles } from "./roles";
import {
  getUserByEmail,
  getUsers,
  getUserById,
  hasUpstreamListPayload,
} from "./api";

export const GOODLEAP_SUBMITTING_USER_DOMAIN = "goodleap.com";

export const submittingUserSubstituteNotFoundMessage =
  "No submitting user found and no eligible substitute user found for configured roles";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export function isGoodleapDomainEmail(email) {
  const normalized = String(email ?? "").trim().toLowerCase();
  if (!normalized.includes("@")) return false;
  return normalized.endsWith(`@${GOODLEAP_SUBMITTING_USER_DOMAIN}`);
}

export function emailsMatch(left, right) {
  return (
    String(left ?? "").trim().toLowerCase() ===
    String(right ?? "").trim().toLowerCase()
  );
}

export function userEmailFromRecord(user, userById) {
  return (userById?.email ?? user?.email ?? "").trim();
}

export function roleNameFromUserRecords(user, userById) {
  const roleName = userById?.role?.name ?? user?.role?.name ?? "";
  return String(roleName).trim();
}

function isResolvedUserRecord(userById) {
  return (
    userById != null &&
    userById !== false &&
    typeof userById === "object" &&
    !Array.isArray(userById)
  );
}

export function salesRepFromUserRecord(user, userById) {
  return {
    firstName: userById?.firstName ?? user?.firstName ?? "",
    lastName: userById?.lastName ?? user?.lastName ?? "",
    email: userEmailFromRecord(user, userById),
    phone:
      userById?.phoneNumber?.number ??
      user?.phoneNumber?.number ??
      user?.phones?.[0]?.number ??
      "",
  };
}

async function fetchWithRetry(fetchFn, attempts = 2, delayMs = 1500) {
  let lastResponse = null;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    lastResponse = await fetchFn();
    if (hasUpstreamListPayload(lastResponse)) {
      return lastResponse;
    }
    if (isResolvedUserRecord(lastResponse)) {
      return lastResponse;
    }
    if (attempt < attempts - 1) {
      await sleep(delayMs);
    }
  }
  return lastResponse;
}

/** True when the configured (DB) submitting user exists in partner API. */
export async function configuredSubmittingUserExists(
  clientId,
  submittingUserEmail,
  token
) {
  const result = await checkAndResolveSubmittingUser(
    clientId,
    submittingUserEmail,
    token
  );
  return result.status === "configured";
}

function findConfiguredUserInList(users, submittingUserEmail) {
  return users.find((user) =>
    emailsMatch(userEmailFromRecord(user, null), submittingUserEmail)
  );
}

function pickSubstituteCandidate(candidates) {
  for (const role of Roles) {
    const match = candidates.find((candidate) => candidate.roleName === role);
    if (match) {
      return {
        substitute: salesRepFromUserRecord(match.user, match.userById),
        roleName: match.roleName,
      };
    }
  }
  return null;
}

/**
 * Substitute when configured user was not found: first match by Roles priority,
 * excluding @goodleap.com emails.
 * @returns {{ substitute: object, roleName: string }|null}
 */
export async function findSubstituteSubmittingUser(clientId, token) {
  const usersResponse = await fetchWithRetry(() => getUsers(clientId, token));
  if (!hasUpstreamListPayload(usersResponse)) return null;

  const result = await findSubstituteSubmittingUserFromList(
    usersResponse.data,
    clientId,
    token
  );
  if (result?.inconclusive) return null;
  return result;
}

async function findSubstituteSubmittingUserFromList(users, clientId, token) {
  if (!users?.length) return null;

  const candidates = [];
  let detailLookupFailed = false;

  for (const user of users) {
    let userById = null;
    if (user?.id) {
      userById = await getUserById(clientId, user.id, token);
      if (!isResolvedUserRecord(userById)) {
        detailLookupFailed = true;
        userById = null;
      }
    }

    const email = userEmailFromRecord(user, userById);
    if (isGoodleapDomainEmail(email)) continue;

    const roleName = roleNameFromUserRecords(user, userById);
    if (!roleName || !Roles.includes(roleName)) continue;

    candidates.push({ user, userById, roleName });
  }

  const substitute = pickSubstituteCandidate(candidates);
  if (substitute) return substitute;

  if (detailLookupFailed && candidates.length === 0) {
    return { inconclusive: true, reason: "user detail lookup failed" };
  }

  return null;
}

/**
 * Resolve configured vs substitute submitting user.
 * Returns inconclusive when partner API data could not be verified (no Slack).
 */
export async function checkAndResolveSubmittingUser(
  clientId,
  submittingUserEmail,
  token
) {
  const email = String(submittingUserEmail ?? "").trim();
  if (!clientId || !email) {
    return { status: "inconclusive", reason: "missing client or email" };
  }

  const byEmail = await fetchWithRetry(() =>
    getUserByEmail(clientId, email, token)
  );
  if (!hasUpstreamListPayload(byEmail)) {
    return { status: "inconclusive", reason: "email lookup unavailable" };
  }

  if (byEmail.data.length > 0) {
    return { status: "configured" };
  }

  const usersResponse = await fetchWithRetry(() => getUsers(clientId, token));
  if (!hasUpstreamListPayload(usersResponse)) {
    return { status: "inconclusive", reason: "user list unavailable" };
  }

  if (findConfiguredUserInList(usersResponse.data, email)) {
    return { status: "configured" };
  }

  const substituteResult = await findSubstituteSubmittingUserFromList(
    usersResponse.data,
    clientId,
    token
  );

  if (substituteResult?.inconclusive) {
    return {
      status: "inconclusive",
      reason: substituteResult.reason ?? "substitute lookup inconclusive",
    };
  }

  if (substituteResult?.substitute) {
    return {
      status: "substituted",
      substitute: substituteResult.substitute,
      roleName: substituteResult.roleName,
    };
  }

  return { status: "not_found" };
}
